import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
    const quillRef = useRef<ReactQuill>(null);

    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            if (input.files) {
                const file = input.files[0];
                const quill = quillRef.current?.getEditor();
                if (!quill) return;

                const range = quill.getSelection(true);
                // Show a loading placeholder
                quill.insertText(range.index, '\n[Uploading image...]\n');

                try {
                    // Upload image to Firebase Storage
                    const storageRef = ref(storage, `content-images/${Date.now()}_${file.name}`);
                    const snapshot = await uploadBytes(storageRef, file);
                    const downloadURL = await getDownloadURL(snapshot.ref);

                    // Replace placeholder with the actual image
                    quill.deleteText(range.index, '\n[Uploading image...]\n'.length);
                    quill.insertEmbed(range.index, 'image', downloadURL);
                    quill.setSelection(range.index + 1, 0);

                } catch (error: any) {
                    console.error("Image upload failed:", error);
                    let message = "Image upload failed. Please try again.";
                    if (error.code === 'storage/unauthorized') {
                        message = 'Permission Denied: Check Firebase Storage rules to allow write access for content images.';
                    }
                    // Replace placeholder with an error message
                    quill.deleteText(range.index, '\n[Uploading image...]\n'.length);
                    quill.insertText(range.index, `\n[Image Upload Failed: ${message}]\n`);
                }
            }
        };
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{'list': 'ordered'}, {'list': 'bullet'}],
                ['link', 'image'],
                ['clean']
            ],
            handlers: {
                image: imageHandler,
            },
        },
    }), []);

    return (
        <div className="bg-white">
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value}
                onChange={onChange}
                modules={modules}
            />
        </div>
    );
};

export default RichTextEditor;